import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  alpha,
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close, OpenInNew } from '@mui/icons-material';
import { getCellColor } from '../utils/cellColors';
import { ratingAsPosition } from '../utils/simColors';
import { DriverTrackRank, getDriverTrackRanks, TrackRankPill } from '../services/api';

type Stat = TrackRankPill['stat'];

const STATS: { key: Stat; label: string; fmt: (v: number) => string }[] = [
  { key: 'finish', label: 'Avg finish', fmt: (v) => v.toFixed(1) },
  { key: 'rating', label: 'Driver rating', fmt: (v) => v.toFixed(1) },
  { key: 'running', label: 'Running position', fmt: (v) => v.toFixed(1) },
];
const rankOf = (t: DriverTrackRank, s: Stat) =>
  s === 'finish' ? t.finishRank : s === 'rating' ? t.ratingRank : t.runningRank;
const valueOf = (t: DriverTrackRank, s: Stat) => (s === 'finish' ? t.finish : s === 'rating' ? t.rating : t.running);
const ordinal = (n: number) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

/** One fixed scale for everyone, so a number always gets the same color: average finish and running position on the
 * table's 1st (green) to 40th (red) scale, driver rating from 130+ (green) down to 40 (red). A driver's best track can
 * still be yellow if it's a mid-pack number. */
const valueColor = (v: number, stat: Stat) => getCellColor(stat === 'rating' ? ratingAsPosition(v) : v, 'position');

/** A driver's tracks ranked by one stat (the pill's, switchable), with the simulated track highlighted: the detail
 * behind a track-rank pill, without leaving the Simulator. */
export const TrackRankDialog: React.FC<{ pill: TrackRankPill; site: string; open: boolean; onClose: () => void }> = ({
  pill,
  site,
  open,
  onClose,
}) => {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const [stat, setStat] = useState<Stat>(pill.stat);
  const q = useQuery({
    queryKey: ['driverTrackRanks', pill.driver],
    queryFn: () => getDriverTrackRanks(pill.driver),
    enabled: open,
    staleTime: 6 * 60 * 60 * 1000,
  });
  const tracks = [...(q.data?.tracks ?? [])].sort((a, b) => rankOf(a, stat) - rankOf(b, stat));
  const n = tracks.length;
  const def = STATS.find((s) => s.key === stat)!;
  const here = tracks.find((t) => t.site === site);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={phone}
      fullWidth
      maxWidth="xs"
      // the dialog sits inside the results row in React's tree: keep its clicks from toggling the row
      onClick={(e) => e.stopPropagation()}
    >
      <DialogTitle sx={{ pr: 6 }}>
        {pill.driver}&apos;s tracks
        <Typography variant="body2" color="text.secondary">
          Since {q.data?.since ?? ''}, tracks with 2+ races
          {here ? ` · ${site} ranks ${ordinal(rankOf(here, stat))} of ${n} by ${def.label.toLowerCase()}` : ''}
        </Typography>
        <IconButton aria-label="Close" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <Close />
        </IconButton>
      </DialogTitle>
      {/* outside the scrolling list, so the stat buttons stay in view */}
      <ToggleButtonGroup
        size="small"
        exclusive
        fullWidth
        value={stat}
        onChange={(_, v) => v && setStat(v)}
        sx={{ px: 3, pb: 1.5 }}
      >
        {STATS.map((s) => (
          <ToggleButton key={s.key} value={s.key} sx={{ textTransform: 'none' }}>
            {phone && s.key === 'running' ? 'Running' : s.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <DialogContent dividers>
        {q.isLoading && (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={24} />
          </Stack>
        )}
        {q.error && <Alert severity="error">{(q.error as Error).message}</Alert>}
        {tracks.map((t, i) => {
          const rank = rankOf(t, stat);
          const fromEnd = n - rank + 1;
          const me = t.site === site;
          const edge = rank <= 3 ? 'success.main' : fromEnd <= 3 ? 'warning.main' : undefined;
          return (
            <Stack
              key={t.site}
              // open (and switch stats) with the simulated track in view, e.g. near the bottom for a "worst" pill
              ref={me ? (el: HTMLDivElement | null) => el?.scrollIntoView({ block: 'nearest' }) : undefined}
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{
                py: 0.6,
                px: 1,
                borderRadius: 1,
                // the simulated track: a green tint, a green edge and a "this race" tag
                bgcolor: me ? (th) => alpha(th.palette.primary.main, 0.16) : i % 2 ? 'action.hover' : undefined,
                boxShadow: me ? (th) => `inset 4px 0 0 ${th.palette.primary.main}` : undefined,
                fontWeight: me ? 700 : 400,
              }}
            >
              <Typography sx={{ width: 34, fontSize: 13, fontWeight: 700, color: edge ?? 'text.secondary' }}>
                {ordinal(rank)}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ minWidth: 0, fontSize: 14, fontWeight: me ? 800 : 500 }} noWrap>
                  {t.site}
                </Typography>
                {me && !phone && (
                  <Box
                    component="span"
                    sx={{
                      flex: 'none',
                      px: 0.75,
                      py: 0.1,
                      borderRadius: 1,
                      fontSize: 10.5,
                      fontWeight: 800,
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                    }}
                  >
                    THIS RACE
                  </Box>
                )}
              </Stack>
              <Box sx={{ width: 64, display: 'flex', justifyContent: 'flex-end' }}>
                <Box
                  component="span"
                  sx={{
                    width: 54, // every chip the same width, whatever the number
                    textAlign: 'center',
                    px: 0.75,
                    py: 0.15,
                    borderRadius: 1,
                    bgcolor: valueColor(valueOf(t, stat), stat),
                    color: '#1b1b1b',
                    fontSize: 13.5,
                    fontWeight: 800,
                  }}
                >
                  {def.fmt(valueOf(t, stat))}
                </Box>
              </Box>
              <Typography sx={{ width: 64, textAlign: 'right', fontSize: 12, color: 'text.secondary' }}>
                {t.races} races
              </Typography>
            </Stack>
          );
        })}
        {n > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              Colors are the same for every driver: finish and running position from 1st (green) to 40th (red), driver
              rating from 130 (green) to 40 (red). The rank is green for their 3 best tracks and orange for their 3
              worst. Lower is better for finish and running position.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          component="a"
          href={`/track-rankings?driver=${encodeURIComponent(pill.driver)}`}
          target="_blank"
          rel="noopener noreferrer"
          endIcon={<OpenInNew fontSize="small" />}
          sx={{ textTransform: 'none' }}
        >
          See full track rankings
        </Button>
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
